import React, { createContext, useState, useContext, useEffect } from 'react';
import { authApi } from '../services/api';

// Create the context
const AuthContext = createContext();

// Create a provider component
export const AuthProvider = ({ children }) => {
  // Initialize state from localStorage if available
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('farmconnect_user');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const [role, setRole] = useState(() => {
    return localStorage.getItem('farmconnect_role') || null;
  });

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('farmconnect_authenticated') === 'true';
  });

  // Update localStorage when auth state changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('farmconnect_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('farmconnect_user');
    }

    if (role) {
      localStorage.setItem('farmconnect_role', role);
    } else {
      localStorage.removeItem('farmconnect_role');
    }

    localStorage.setItem('farmconnect_authenticated', isAuthenticated.toString());
  }, [user, role, isAuthenticated]);

  // Login function that validates credentials against the backend
  const login = async (username, password, userRole) => {
    try {
      console.log('Attempting login with:', { username, role: userRole });

      // Clear any existing auth data first
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('farmconnect_user');
      localStorage.removeItem('farmconnect_role');
      localStorage.setItem('farmconnect_authenticated', 'false');

      // Call the authentication API
      const response = await authApi.login(username, password, userRole);
      console.log('Login response:', response);

      if (response.success) {
        const userData = response.user;
        const authenticatedRole = userData.role || response.role;
        const dashboardUrl = response.dashboard_url;

        console.log('Raw user data:', userData);
        console.log('User role:', authenticatedRole);

        // Ensure company ID is set for company users
        if (authenticatedRole === 'company') {
          console.log('Processing company user data');
          console.log('Company data:', userData.company);
          
          // Try multiple ways to get the company ID
          if (userData.company) {
            if (typeof userData.company === 'object' && userData.company.id) {
              userData.companyId = userData.company.id;
            } else if (typeof userData.company === 'number' || typeof userData.company === 'string') {
              userData.companyId = userData.company;
            }
          } else if (userData.company_id) {
            userData.companyId = userData.company_id;
          } else if (userData.companyId) {
            userData.companyId = userData.companyId;
          }

          console.log('Set company ID:', userData.companyId);

          if (!userData.companyId) {
            console.error('No company ID found in user data');
            throw new Error('Company ID not found in user data. Please contact support.');
          }
        }

        console.log('Final user data:', userData);
        console.log('User authenticated with role:', authenticatedRole);
        console.log('Dashboard URL:', dashboardUrl);

        // Set the authenticated user data
        setUser(userData);
        setRole(authenticatedRole);
        setIsAuthenticated(true);

        // Store in localStorage
        localStorage.setItem('farmconnect_user', JSON.stringify(userData));
        localStorage.setItem('farmconnect_role', authenticatedRole);
        localStorage.setItem('farmconnect_authenticated', 'true');

        // Verify token is stored
        const token = localStorage.getItem('access_token');
        if (!token) {
          console.error('No access token found after login');
          throw new Error('Authentication failed - no token received');
        }

        return {
          success: true,
          dashboardUrl
        };
      } else {
        console.error('Authentication failed:', response.error);
        // Clear any existing auth data
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('farmconnect_user');
        localStorage.removeItem('farmconnect_role');
        localStorage.setItem('farmconnect_authenticated', 'false');
        return {
          success: false,
          error: response.error
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      // Clear any existing auth data
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('farmconnect_user');
      localStorage.removeItem('farmconnect_role');
      localStorage.setItem('farmconnect_authenticated', 'false');
      return {
        success: false,
        error: error.message || 'An error occurred during login'
      };
    }
  };

  // Logout function
  const logout = () => {
    authApi.logout();
    setUser(null);
    setRole(null);
    setIsAuthenticated(false);

    // Clear localStorage
    localStorage.removeItem('farmconnect_user');
    localStorage.removeItem('farmconnect_role');
    localStorage.setItem('farmconnect_authenticated', 'false');
  };

  // Get user profile
  const getProfile = async () => {
    try {
      const response = await authApi.getProfile();
      if (response.success) {
        setUser(response.user);
        return response.user;
      }
      return null;
    } catch (error) {
      console.error('Get profile error:', error);
      return null;
    }
  };

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      const response = await authApi.updateProfile(userData);
      if (response.success) {
        setUser(response.user);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Update profile error:', error);
      return false;
    }
  };

  // Get company profile
  const getCompanyProfile = async () => {
    try {
      const response = await authApi.getCompanyProfile();
      if (response.success) {
        return response.profile;
      }
      return null;
    } catch (error) {
      console.error('Get company profile error:', error);
      return null;
    }
  };

  // Update company profile
  const updateCompanyProfile = async (profileData) => {
    try {
      const response = await authApi.updateCompanyProfile(profileData);
      if (response.success) {
        return response.profile;
      }
      return null;
    } catch (error) {
      console.error('Update company profile error:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        isAuthenticated,
        login,
        logout,
        getProfile,
        updateProfile,
        getCompanyProfile,
        updateCompanyProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;

